export const saveWorkSpaceToLS = (workspaceId: string) => {
    localStorage.setItem('workspaceId', workspaceId)
}

export const getWorkSpaceFromLS = (): string => {
    return localStorage.getItem('workspaceId') || ''
}

export const removeWorkSpaceFromLS = () => {
    localStorage.removeItem('workspaceId')
}

export const saveWorkspaceViewState = (workspaceId: string, viewState: any) => {
    const existingStates = getWorkspaceViewStates();
    existingStates[workspaceId] = {
        ...existingStates[workspaceId],
        ...viewState,
        workspaceId
    };
    localStorage.setItem('workspaceViewStates', JSON.stringify(existingStates));
}

export const getWorkspaceViewStates = (): { [workspaceId: string]: any } => {
    const states = localStorage.getItem('workspaceViewStates');
    return states ? JSON.parse(states) : {};
}

export const getWorkspaceViewState = (workspaceId: string): any => {
    const states = getWorkspaceViewStates();
    return states[workspaceId] || null;
}

export const removeWorkspaceViewState = (workspaceId: string) => {
    const states = getWorkspaceViewStates();
    delete states[workspaceId];
    localStorage.setItem('workspaceViewStates', JSON.stringify(states));
}

// Workspace permission utilities
export const hasWriteAccess = (workspace: import('store/workspace/types').IWorkspaceItem | null, currentUserId?: string): boolean => {
    if (!workspace) return false;
    
    // Owner always has write access
    if (workspace.isOwner) return true;
    
    // Check if user has write access level
    if (workspace.accessLevel === 'write') return true;
    
    // For backward compatibility, if no accessLevel is set, assume write access for shared users
    if (!workspace.accessLevel && workspace.sharedWith?.some(user => user.id === currentUserId)) {
        return true;
    }
    
    return false;
};

export const isReadOnlyAccess = (workspace: import('store/workspace/types').IWorkspaceItem | null, currentUserId?: string): boolean => {
    if (!workspace) return false;
    
    // Owner never has read-only access
    if (workspace.isOwner) return false;
    
    // Check if user has read-only access level
    if (workspace.accessLevel === 'read') return true;
    
    return false;
};

export const getWorkspaceAccessLevel = (workspace: import('store/workspace/types').IWorkspaceItem | null, currentUserId?: string): 'owner' | 'write' | 'read' | 'none' => {
    if (!workspace) return 'none';
    
    if (workspace.isOwner) return 'owner';
    
    if (workspace.accessLevel === 'read') return 'read';
    if (workspace.accessLevel === 'write') return 'write';
    
    // For backward compatibility, check if user is in shared list
    if (workspace.sharedWith?.some(user => user.id === currentUserId)) {
        return 'write'; // Default to write access for existing shared users
    }
    
    return 'none';
};

// Helper function to get workspace owner email
export const getWorkspaceOwnerEmail = (workspace: import('store/workspace/types').IWorkspaceItem | null): string => {
    if (!workspace || !workspace.createdBy) return '';
    return workspace.createdBy.name || '';
};

// Helper function to check read-only access and show error popup if needed
export const checkAndHandleReadOnlyAccess = (
    workspace: import('store/workspace/types').IWorkspaceItem | null, 
    currentUserId?: string,
    dispatch?: any
): boolean => {
    if (isReadOnlyAccess(workspace, currentUserId)) {
        if (dispatch) {
            const ownerEmail = getWorkspaceOwnerEmail(workspace);
            const message = ownerEmail 
                ? `This is a read-only workspace, and therefore cannot be edited.\n\nThis workspace is owned by ${ownerEmail}`
                : 'This is a read-only workspace, and therefore cannot be edited.';
            
            // Dispatch the restriction popup action
            dispatch({ type: 'workspace/handleRestrictionPopup', payload: { open: true, message } });
        }
        return true; // Is read-only
    }
    return false; // Not read-only, can proceed
};

// Helper function for use in components with proper dispatch typing
export const showReadOnlyError = (workspace: import('store/workspace/types').IWorkspaceItem | null, dispatch: any) => {
    const ownerEmail = getWorkspaceOwnerEmail(workspace);
    const message = ownerEmail 
        ? `This is a read-only workspace, and therefore cannot be edited.\n\nThis workspace is owned by ${ownerEmail}`
        : 'This is a read-only workspace, and therefore cannot be edited.';
    
    // For components that have access to the workspaceSlice actions directly
    return { open: true, message };
};